import videojs from "video.js";
import * as customUtilsDom from "./custom-utils-dom";

const Fn = {
    bind: videojs.bind,
};
const Dom = videojs.dom;
const ProgressControl = videojs.getComponent("ProgressControl");
const Slider = videojs.getComponent("Slider");
const TimeTooltip = videojs.getComponent("TimeTooltip");
const Player = videojs.getComponent("Player");

videojs.dom = {
    ...videojs.dom,
    findPosition: customUtilsDom.findPosition,
    getPointerPosition: customUtilsDom.getPointerPosition,
};

Slider.prototype.calculateDistance = function calculateDistance(event) {
    const position = customUtilsDom.getPointerPosition(this.el_, event);

    if (this.vertical()) {
        return position.y;
    }
    return position.x;
};

ProgressControl.prototype.handleMouseMove = function handleMouseMove(event) {
    const seekBar = this.getChild("seekBar");

    if (seekBar) {
        const mouseTimeDisplay = seekBar.getChild("mouseTimeDisplay");
        const seekBarEl = seekBar.el();
        const seekBarRect = customUtilsDom.findPosition(seekBarEl);
        let seekBarPoint = customUtilsDom.getPointerPosition(seekBarEl, event).x;

        // The default skin has a gap on either side of the `SeekBar`. This means
        // that it's possible to trigger this behavior outside the boundaries of
        // the `SeekBar`. This ensures we stay within it at all times.
        if (seekBarPoint > 1) {
            seekBarPoint = 1;
        } else if (seekBarPoint < 0) {
            seekBarPoint = 0;
        }

        if (mouseTimeDisplay) {
            mouseTimeDisplay.update(seekBarRect, seekBarPoint);
        }
    }
};

/**
 * Updates the position of the time tooltip relative to the `SeekBar`.
 *
 * @param {Object} seekBarRect
 *        The `ClientRect` for the {@link SeekBar} element.
 *
 * @param {number} seekBarPoint
 *        A number from 0 to 1, representing a horizontal reference point
 *        from the left edge of the {@link SeekBar}
 */
TimeTooltip.prototype.update = function update(seekBarRect, seekBarPoint, content) {
    const tooltipRect = customUtilsDom.findPosition(this.el_);
    const playerRect = Dom.getBoundingClientRect(this.player_.el());
    const seekBarPointPx = seekBarRect.width * seekBarPoint;

    // do nothing if either rect isn't available
    // for example, if the player isn't in the DOM for testing
    if (!playerRect || !tooltipRect) {
        return;
    }

    // This is the space left of the `seekBarPoint` available within the bounds
    // of the player. We calculate any gap between the left edge of the player
    // and the left edge of the `SeekBar` and add the number of pixels in the
    // `SeekBar` before hitting the `seekBarPoint`
    const spaceLeftOfPoint = seekBarRect.left - playerRect.left + seekBarPointPx;

    // This is the space right of the `seekBarPoint` available within the bounds
    // of the player. We calculate the number of pixels from the `seekBarPoint`
    // to the right edge of the `SeekBar` and add to that any gap between the
    // right edge of the `SeekBar` and the player.
    const spaceRightOfPoint = seekBarRect.width - seekBarPointPx + (playerRect.right - seekBarRect.right);

    // This is the number of pixels by which the tooltip will need to be pulled
    // further to the right to center it over the `seekBarPoint`.
    let pullTooltipBy = tooltipRect.width / 2;

    // Adjust the `pullTooltipBy` distance to the left or right depending on
    // the results of the space calculations above.
    if (spaceLeftOfPoint < pullTooltipBy) {
        pullTooltipBy += pullTooltipBy - spaceLeftOfPoint;
    } else if (spaceRightOfPoint < pullTooltipBy) {
        pullTooltipBy = spaceRightOfPoint;
    }

    // Due to the imprecision of decimal/ratio based calculations and varying
    // rounding behaviors, there are cases where the spacing adjustment is off
    // by a pixel or two. This adds insurance to these calculations.
    if (pullTooltipBy < 0) {
        pullTooltipBy = 0;
    } else if (pullTooltipBy > tooltipRect.width) {
        pullTooltipBy = tooltipRect.width;
    }

    this.el_.style.right = `-${pullTooltipBy}px`;
    Dom.textContent(this.el_, content);
};

/**
 * Listen for user activity based on timeout value
 *
 * @private
 */
Player.prototype.listenForUserActivity_ = function listenForUserActivity_() {
    let mouseInProgress;
    let lastMoveX;
    let lastMoveY;
    const handleActivity = Fn.bind(this, this.reportUserActivity);

    const handleMouseMove = function(e) {
        // #1068 - Prevent mousemove spamming
        // Chrome Bug: https://code.google.com/p/chromium/issues/detail?id=366970
        if (e.screenX !== lastMoveX || e.screenY !== lastMoveY) {
            lastMoveX = e.screenX;
            lastMoveY = e.screenY;
            handleActivity();
        }
    };

    const handleMouseDown = function() {
        handleActivity();
        // For as long as the they are touching the device or have their mouse down,
        // we consider them active even if they're not moving their finger or mouse.
        // So we want to continue to update that they are active
        this.clearInterval(mouseInProgress);
        // Setting userActivity=true now and setting the interval to the same time
        // as the activityCheck interval (250) should ensure we never miss the
        // next activityCheck
        mouseInProgress = this.setInterval(handleActivity, 250);
    };

    // eslint-disable-next-line
    const handleMouseUp = function(event) {
        handleActivity();
        // Stop the interval that maintains activity if the mouse/touch is down
        this.clearInterval(mouseInProgress);
    };

    // Any mouse movement will be considered user activity
    this.on("mousedown", handleMouseDown);
    this.on("mousemove", handleMouseMove);
    this.on("mouseup", handleMouseUp);

    const controlBar = this.getChild("controlBar");

    if (controlBar) {
        this.player().cache_.inactivityTimeout = this.player().options_.inactivityTimeout;

        // eslint-disable-next-line
        controlBar.on("mouseenter", function(event) {
            this.player().cache_.inactivityTimeout = this.player().options_.inactivityTimeout;
            this.player().options_.inactivityTimeout = 0;
        });

        // eslint-disable-next-line
        controlBar.on("mouseleave", function(event) {
            this.player().options_.inactivityTimeout = this.player().cache_.inactivityTimeout;
        });
    }

    // Listen for keyboard navigation
    // Shouldn't need to use inProgress interval because of key repeat
    this.on("keydown", handleActivity);
    this.on("keyup", handleActivity);

    // Run an interval every 250 milliseconds instead of stuffing everything into
    // the mousemove/touchmove function itself, to prevent performance degradation.
    // `this.reportUserActivity` simply sets this.userActivity_ to true, which
    // then gets picked up by this loop
    // http://ejohn.org/blog/learning-from-twitter/
    let inactivityTimeout;

    this.setInterval(function() {
        // Check to see if mouse/touch activity has happened
        if (!this.userActivity_) {
            return;
        }

        // Reset the activity tracker
        this.userActivity_ = false;

        // If the user state was inactive, set the state to active
        this.userActive(true);

        // Clear any existing inactivity timeout to start the timer over
        this.clearTimeout(inactivityTimeout);

        const timeout = this.options_.inactivityTimeout;

        if (timeout <= 0) {
            return;
        }

        // In <timeout> milliseconds, if no more activity has occurred the
        // user will be considered inactive
        inactivityTimeout = this.setTimeout(function() {
            // Protect against the case where the inactivityTimeout can trigger just
            // before the next user activity is picked up by the activity check loop
            // causing a flicker
            if (!this.userActivity_) {
                this.userActive(false);
            }
        }, timeout);
    }, 250);
};

export default videojs;
